import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiChevronDown,
  FiChevronUp,
  FiEdit2,
  FiInfo,
  FiPlus,
  FiTrash2,
  FiUser,
  FiX,
} from "react-icons/fi";
import { IoAlertCircleOutline } from "react-icons/io5";
import { MdClose, MdOutlineBusiness } from "react-icons/md";
import { v4 as uuidv4 } from "uuid";
import { CounterPartySelect } from "../../../components/Common/CounterPartySelection";
import { getCurrentUserId } from "../../../utils";
import { getAddressDetails } from "../AddressBook/actions";

const ExpensesInvoiceModel = ({
  isReadOnly = false,
  vesselType = "",
  isVesselVesselOps = false,
  invoiceType = "Expense",
  expensesInvoiceDetails = {},
  vesselDetails = {},
  expenseAllocations = [],
  setExpenseAllocations,
  usedCounterparties = {},
  setUsedCounterparties,
}) => {
  const [expandedFields, setExpandedFields] = useState([]);
  const [counterPartyDetails, setCounterPartyDetails] = useState([]);
  const [counterpartyModal, setCounterpartyModal] = useState(null);

  const vesselName =
    (vesselDetails?.vesselDetails?.vessel &&
      JSON.parse(vesselDetails?.vesselDetails?.vessel || "{}")?.vesselLabel) ||
    "vessel";

  const expenseFields = useMemo(() => [
    { key: "grossHireAmount", label: "Gross Hire Amount" },
    { key: "adCom", label: "Adcom" },
    { key: "brokerageExp", label: "Brokerage" },
    { key: "deduction", label: "Deduction" },
    { key: "totalNetHireExpense", label: "Total Net Hire Expense" },
    { key: "cve", label: "CVE" },
    { key: "portExpenses", label: "Port Expenses" },
    { key: "otherExp", label: "Other Exp" },
    ...(isVesselVesselOps
      ? [
        { key: "cvePerDay", label: "CVE Per Day" },
        { key: "ilohc", label: "ILOHC" },
      ]
      : []),
  ], [isVesselVesselOps]);

  // Fetch address book counterparties for enriching details (bank, address, etc.)
  const fetchCounterPartyDetails = useCallback(async () => {
    const userId = getCurrentUserId();
    if (!userId) return;
    try {
      const userRes = await getAddressDetails({
        userId,
        addressBookType: "user",
        perPage: 100,
      });
      setCounterPartyDetails(userRes?.data?.list || []);
    } catch (error) {
      console.error("Failed to fetch counter party details:", error);
      setCounterPartyDetails([]);
    }
  }, []);

  useEffect(() => {
    fetchCounterPartyDetails();
  }, [fetchCounterPartyDetails]);

  // Filter counterparties based on expense type and vessel contract selections
  const getAvailableCounterparties = useCallback((fieldKey, selectedId = null) => {
    const usedInThisField = usedCounterparties?.[fieldKey] || new Set();
    const counterPartyNames = vesselDetails?.counterPartyNames || [];
    const brokers = vesselDetails?.brokers || [];
    const organizationAddress = vesselDetails?.organizationAddress || [];
    const myAddressBook = vesselDetails?.myAddressBook || [];

    const mergeUniqueById = (...arrays) => {
      const map = new Map();
      arrays.flat().forEach((item) => {
        const id = item?._id || item?.id;
        if (id && !map.has(String(id))) {
          map.set(String(id), { ...item, id: String(id), _id: String(id) });
        }
      });
      return Array.from(map.values());
    };

    let allCounterparties = [];
    if (fieldKey === "brokerageExp") {
      allCounterparties = mergeUniqueById(brokers);
    } else {
      const brokerIds = new Set(
        brokers.map((b) => String(b._id || b.id)).filter(Boolean)
      );
      allCounterparties = mergeUniqueById(
        counterPartyNames,
        organizationAddress,
        myAddressBook
      ).filter((cp) => !brokerIds.has(String(cp._id || cp.id)));
    }

    // Filter by not used in this field (except currently selected on this row)
    const filtered = allCounterparties.filter((cp) => {
      const cpId = String(cp._id || cp.id);
      return !usedInThisField.has(cpId) || (selectedId && cpId === String(selectedId));
    });

    // Enrich with full address book details if available
    return filtered.map((cp) => {
      const cpId = String(cp._id || cp.id);
      const addr = (counterPartyDetails || []).find(
        (d) => String(d._id || d.id) === cpId
      );

      if (addr) {
        return {
          ...addr,
          ...cp,
          _id: cpId,
          id: cpId,
          type: addr.type || cp.type || "Organisation",
          organisation_fullName: addr.organisation_fullName || cp.name || cp.fullName || "Unknown",
          fullName: addr.fullName || cp.name || cp.fullName || "Unknown",
          name: cp.name || addr.fullName || "Unknown",
        };
      }

      return {
        _id: cpId,
        id: cpId,
        type: cp.type || "Organisation",
        organisation_fullName: cp.name || cp.fullName || "Unknown",
        organisation_shortName: cp.shortName || cp.name?.substring(0, 4)?.toUpperCase() || "ORG",
        fullName: cp.name || cp.fullName || "Unknown",
        name: cp.name || "Unknown",
        ...cp,
      };
    });
  }, [counterPartyDetails, usedCounterparties, vesselDetails]);

  // Combined master list of contract counterparties for display resolution
  const allCounterPartyList = useMemo(() => {
    const fromVessel = [
      ...(vesselDetails?.counterPartyNames || []),
      ...(vesselDetails?.brokers || []),
      ...(vesselDetails?.organizationAddress || []),
      ...(vesselDetails?.myAddressBook || []),
    ];

    const map = new Map();

    fromVessel.forEach((item) => {
      const id = item?._id || item?.id;
      if (id && !map.has(String(id))) {
        map.set(String(id), {
          _id: String(id),
          id: String(id),
          type: item.type || "Organisation",
          organisation_fullName: item.name || item.fullName || item.organisation_fullName || "Unknown",
          fullName: item.name || item.fullName || "Unknown",
          ...item,
        });
      }
    });

    return Array.from(map.values());
  }, [vesselDetails]);

  const createEmptyRow = useCallback((fieldKey) => ({
    _id: uuidv4(),
    label: expenseFields.find((f) => f.key === fieldKey)?.label,
    vesselContractId: vesselDetails?.vesselContractId,
    vesselType,
    counterPartyId: "",
    name: "",
    description: "",
    amt: 0,
    isCargo: false,
  }), [expenseFields, vesselDetails, vesselType]);

  const ensureFieldEntry = useCallback((prev, fieldKey) => {
    const exists = prev.some((item) => item.key === fieldKey);
    if (exists) return prev;
    return [
      ...prev,
      {
        vesselId: vesselDetails?.vesselId || "",
        key: fieldKey,
        vesselType,
        totalAmt: 0,
        vesselName,
        label: expenseFields.find((f) => f.key === fieldKey)?.label,
        invoiceDetails: [createEmptyRow(fieldKey)],
      },
    ];
  }, [createEmptyRow, expenseFields, vesselDetails, vesselName, vesselType]);

  const toggleField = (key) => {
    setExpandedFields((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

    if (!expandedFields.includes(key)) {
      setExpenseAllocations((prev) => {
        const exists = prev.some((item) => item.key === key);
        if (exists) return prev;
        return [
          ...prev,
          {
            vesselId: vesselDetails?.vesselId || "",
            key,
            vesselType,
            totalAmt: 0,
            vesselName,
            label: expenseFields.find((f) => f.key === key)?.label,
            invoiceDetails: [createEmptyRow(key)],
          },
        ];
      });
    }
  };

  // Normalize duplicate entries
  useEffect(() => {
    if (!expenseAllocations || expenseAllocations.length === 0) return;

    let hasDuplicates = false;
    const keyMap = {};
    const normalized = [];

    expenseAllocations.forEach((item) => {
      if (keyMap[item.key]) {
        hasDuplicates = true;
        const existing = keyMap[item.key];
        existing.invoiceDetails = [
          ...(existing.invoiceDetails || []),
          ...(item.invoiceDetails || []),
        ];
        existing.totalAmt = (existing.totalAmt || 0) + (item.totalAmt || 0);
      } else {
        keyMap[item.key] = {
          ...item,
          invoiceDetails: [...(item.invoiceDetails || [])],
        };
        normalized.push(keyMap[item.key]);
      }
    });

    if (hasDuplicates) {
      setExpenseAllocations(normalized);
    }
  }, [expenseAllocations, setExpenseAllocations]);

  // Sync used counterparties
  useEffect(() => {
    if (!expenseAllocations || expenseAllocations.length === 0) return;
    const newUsed = {};
    expenseAllocations.forEach((item) => {
      const usedIds = new Set();
      item.invoiceDetails?.forEach((detail) => {
        if (detail.counterPartyId) {
          usedIds.add(String(detail.counterPartyId));
        }
      });
      if (usedIds.size > 0) {
        newUsed[item.key] = usedIds;
      }
    });
    setUsedCounterparties((prev) => {
      const prevKeys = Object.keys(prev || {});
      const newKeys = Object.keys(newUsed);
      if (prevKeys.length !== newKeys.length) return newUsed;
      for (const key of newKeys) {
        const prevSet = prev[key];
        const newSet = newUsed[key];
        if (!prevSet || prevSet.size !== newSet.size) return newUsed;
        for (const id of newSet) {
          if (!prevSet.has(id)) return newUsed;
        }
      }
      return prev;
    });
  }, [expenseAllocations, setUsedCounterparties]);

  const totals = useMemo(() => {
    const map = {};
    expenseAllocations?.forEach((item) => {
      const currentSum =
        item.invoiceDetails?.reduce(
          (sum, detail) => sum + (Number(detail.amt) || 0),
          0
        ) || 0;
      map[item.key] = (map[item.key] || 0) + currentSum;
    });
    return map;
  }, [expenseAllocations]);

  const handleAddRow = (fieldKey) => {
    setExpenseAllocations((prev) => {
      const withEntry = ensureFieldEntry(prev, fieldKey);
      return withEntry.map((item) => {
        if (item.key === fieldKey) {
          return {
            ...item,
            vesselName,
            label: expenseFields.find((f) => f.key === fieldKey)?.label,
            invoiceDetails: [...(item.invoiceDetails || []), createEmptyRow(fieldKey)],
          };
        }
        return item;
      });
    });
  };

  const handleDeleteRow = (fieldKey, index) => {
    setExpenseAllocations((prev) =>
      prev.map((item) => {
        if (item.key === fieldKey) {
          const newInvoiceDetails = item.invoiceDetails.filter(
            (_, i) => i !== index
          );

          const finalDetails =
            newInvoiceDetails.length > 0
              ? newInvoiceDetails
              : [createEmptyRow(fieldKey)];

          return {
            ...item,
            invoiceDetails: finalDetails,
          };
        }
        return item;
      })
    );

    setUsedCounterparties((prev) => {
      const fieldAllocations = prev?.[fieldKey];
      const deletedItem = expenseAllocations.find(
        (item) => item.key === fieldKey
      )?.invoiceDetails?.[index];

      if (deletedItem?.counterPartyId && fieldAllocations) {
        const newSet = new Set(fieldAllocations);
        newSet.delete(String(deletedItem.counterPartyId));
        return {
          ...prev,
          [fieldKey]: newSet,
        };
      }
      return prev;
    });
  };

  const handleCounterpartySelect = (fieldKey, index, counterPartyId) => {
    const available = getAvailableCounterparties(fieldKey, counterPartyId);
    const selectedCounterparty =
      available.find((cp) => String(cp._id || cp.id) === String(counterPartyId)) ||
      allCounterPartyList.find((cp) => String(cp._id || cp.id) === String(counterPartyId));

    const name = selectedCounterparty
      ? selectedCounterparty.type === "Individual"
        ? `${selectedCounterparty.individual_firstName || ""} ${selectedCounterparty.individual_lastName || ""}`.trim() || selectedCounterparty.fullName || selectedCounterparty.name
        : selectedCounterparty.organisation_fullName || selectedCounterparty.organisation_name || selectedCounterparty.fullName || selectedCounterparty.name || "Unknown Organization"
      : "";

    const currentCounterpartyId = expenseAllocations.find(
      (item) => item.key === fieldKey
    )?.invoiceDetails?.[index]?.counterPartyId;

    setExpenseAllocations((prev) => {
      const withEntry = ensureFieldEntry(prev, fieldKey);
      return withEntry.map((item) =>
        item.key === fieldKey
          ? {
            ...item,
            invoiceDetails: (item.invoiceDetails || []).map((detail, i) =>
              i === index
                ? {
                  ...detail,
                  label: expenseFields.find((f) => f.key === fieldKey)?.label,
                  counterPartyId: counterPartyId,
                  name: name,
                }
                : detail
            ),
          }
          : item
      );
    });

    setUsedCounterparties((prev) => {
      const newState = { ...(prev || {}) };
      const fieldSet = new Set(newState[fieldKey] || []);
      if (currentCounterpartyId) {
        fieldSet.delete(String(currentCounterpartyId));
      }
      if (counterPartyId) {
        fieldSet.add(String(counterPartyId));
      }
      newState[fieldKey] = fieldSet;
      return newState;
    });
  };

  const handleClearCounterparty = (fieldKey, index) => {
    const currentCounterpartyId = expenseAllocations.find(
      (item) => item.key === fieldKey
    )?.invoiceDetails?.[index]?.counterPartyId;

    setExpenseAllocations((prev) => {
      const withEntry = ensureFieldEntry(prev, fieldKey);
      return withEntry.map((item) =>
        item.key === fieldKey
          ? {
            ...item,
            invoiceDetails: (item.invoiceDetails || []).map((detail, i) =>
              i === index
                ? {
                  ...detail,
                  counterPartyId: "",
                  name: "",
                }
                : detail
            ),
          }
          : item
      );
    });

    if (currentCounterpartyId) {
      setUsedCounterparties((prev) => {
        const newState = { ...(prev || {}) };
        const fieldSet = new Set(newState[fieldKey] || []);
        fieldSet.delete(String(currentCounterpartyId));
        newState[fieldKey] = fieldSet;
        return newState;
      });
    }
  };

  const handleDescriptionChange = (fieldKey, index, value) => {
    setExpenseAllocations((prev) => {
      const withEntry = ensureFieldEntry(prev, fieldKey);
      return withEntry.map((item) =>
        item.key === fieldKey
          ? {
            ...item,
            invoiceDetails: (item.invoiceDetails || []).map((detail, i) =>
              i === index
                ? {
                  ...detail,
                  description: value,
                  desc: value,
                }
                : detail
            ),
          }
          : item
      );
    });
  };

  const handleAmountChange = (fieldKey, index, value) => {
    const amount = parseFloat(value) || 0;
    const fieldTotal = Number(expensesInvoiceDetails?.[fieldKey]) || 0;

    setExpenseAllocations((prev) => {
      const withEntry = ensureFieldEntry(prev, fieldKey);
      return withEntry.map((item) => {
        if (item.key === fieldKey) {
          const otherAllocations = (item.invoiceDetails || [])
            .filter((_, i) => i !== index)
            .reduce((sum, detail) => sum + (Number(detail.amt) || 0), 0);
          const maxAllowed = Math.max(0, fieldTotal - otherAllocations);
          const finalAmount = Math.min(amount, maxAllowed);

          return {
            ...item,
            invoiceDetails: (item.invoiceDetails || []).map((detail, i) =>
              i === index
                ? {
                  ...detail,
                  amt: finalAmount,
                  label: expenseFields.find((f) => f.key === fieldKey)?.label,
                }
                : detail
            ),
          };
        }
        return item;
      });
    });
  };

  const formatNumber = (num) => {
    const val = Number(num) || 0;
    return val.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div className="bg-white rounded-none border border-[#cbd5e1] overflow-hidden">
      {/* Main Expenses Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-[11px] font-medium">
          <thead className="bg-[#e2e8f0] text-[#1e293b]">
            <tr>
              <th scope="col" className="px-2 py-0.5 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">
                {invoiceType} Type
              </th>
              <th scope="col" className="px-2 py-0.5 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">
                Total Amount
              </th>
              <th scope="col" className="px-2 py-0.5 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">
                Allocated Amount
              </th>
              <th scope="col" className="px-2 py-0.5 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1]">
                Status & Progress
              </th>
              <th scope="col" className="px-2 py-0.5 text-center font-medium text-[11px] text-[#1e293b] border-b border-[#cbd5e1]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {expenseFields.map((field) => {
              const fieldData = expenseAllocations.find((item) => item.key === field.key);
              const fieldTotal = Number(expensesInvoiceDetails?.[field.key]) || 0;
              const allocatedTotal = Number(totals[field.key]) || 0;
              const isOverallocated = allocatedTotal > fieldTotal;
              const isFullyAllocated = fieldTotal > 0 && Math.abs(allocatedTotal - fieldTotal) < 0.01;
              const canAddCounterparty = fieldTotal > 0;
              const isExpanded = expandedFields.includes(field.key);
              const remainingAmount = Math.max(0, fieldTotal - allocatedTotal);
              const availableCounterpartiesForField = getAvailableCounterparties(field.key);

              const percentAllocated = fieldTotal > 0 ? Math.min(100, Math.round((allocatedTotal / fieldTotal) * 100)) : 0;

              return (
                <React.Fragment key={field.key}>
                  {/* Expense Type Row */}
                  <tr
                    className={`transition-colors cursor-pointer select-none border-b border-[#cbd5e1] ${isExpanded ? "bg-[#f0f4f9]" : "hover:bg-slate-50/50"
                      }`}
                    onClick={() => toggleField(field.key)}
                  >
                    <td className="px-2 py-0.5 whitespace-nowrap border-r border-[#cbd5e1]">
                      <div className="flex items-center gap-2">
                        <span className={`p-0.5 rounded-none transition-colors ${isExpanded ? "bg-slate-200 text-[#313C4C]" : "text-slate-400"}`}>
                          {isExpanded ? (
                            <FiChevronUp className="h-3.5 w-3.5" />
                          ) : (
                            <FiChevronDown className="h-3.5 w-3.5" />
                          )}
                        </span>
                        <span className="font-medium text-slate-800 text-[11px]">
                          {field.label}
                        </span>
                      </div>
                    </td>
                    <td className="px-2 py-0.5 whitespace-nowrap font-medium text-slate-800 text-[11px] border-r border-[#cbd5e1]">
                      {formatNumber(fieldTotal)}
                    </td>
                    <td className="px-2 py-0.5 whitespace-nowrap border-r border-[#cbd5e1]">
                      {isOverallocated ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded-sm text-[10px] font-medium bg-rose-50 text-rose-800 border border-rose-300">
                          {formatNumber(allocatedTotal)} (Over)
                        </span>
                      ) : isFullyAllocated ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded-sm text-[10px] font-medium bg-emerald-50 text-emerald-800 border border-emerald-300">
                          {formatNumber(allocatedTotal)}
                        </span>
                      ) : allocatedTotal > 0 ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded-sm text-[10px] font-medium bg-sky-50 text-[#1e293b] border border-sky-300">
                          {formatNumber(allocatedTotal)}
                        </span>
                      ) : (
                        <span className="text-[11px] font-medium text-slate-400">
                          0.00
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-0.5 whitespace-nowrap border-r border-[#cbd5e1]">
                      {isOverallocated ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-100 text-rose-800 border border-rose-200">
                          Overallocated
                        </span>
                      ) : isFullyAllocated ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                          100% Paid
                        </span>
                      ) : allocatedTotal > 0 ? (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-900 border border-amber-200">
                          Bal: ${formatNumber(remainingAmount)}
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">
                          Unallocated
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-0.5 whitespace-nowrap text-center">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleField(field.key);
                        }}
                        className="inline-flex items-center justify-center text-[11px] font-medium text-[#1e293b] underline cursor-pointer bg-transparent border-none p-0 outline-none whitespace-nowrap select-none"
                      >
                        {isExpanded ? "Hide Details" : "View Details"}
                      </button>
                    </td>
                  </tr>

                  {/* Expanded Sub-Table */}
                  {isExpanded && (
                    <tr className="bg-slate-50/50">
                      <td colSpan={5} className="p-2">
                        {!canAddCounterparty ? (
                          <div className="flex items-center gap-2 p-2 rounded-none bg-amber-50 border border-amber-200 text-[11px] text-amber-800 font-medium">
                            <IoAlertCircleOutline className="w-4 h-4 text-amber-600 flex-shrink-0" />
                            <span>
                              No total amount allocated for <strong className="font-medium text-amber-900">{field.label}</strong>. Set an amount in actual values to enable allocations.
                            </span>
                          </div>
                        ) : (
                          <div className="bg-white border border-[#cbd5e1] rounded-none overflow-hidden">
                            {/* Subtable Header */}
                            <div className="overflow-x-auto">
                              <table className="min-w-full border-collapse text-[11px] font-medium">
                                <thead className="bg-[#e2e8f0] text-[#1e293b]">
                                  <tr>
                                    <th scope="col" className="px-2 py-0.5 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] w-[32%]">
                                      Counterparty
                                    </th>
                                    <th scope="col" className="px-2 py-0.5 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] w-[36%]">
                                      Description
                                    </th>
                                    <th scope="col" className="px-2 py-0.5 text-left font-medium text-[11px] text-[#1e293b] border-r border-b border-[#cbd5e1] w-[22%]">
                                      Amount
                                    </th>
                                    <th scope="col" className="px-2 py-0.5 text-right font-medium text-[11px] text-[#1e293b] border-b border-[#cbd5e1] w-[10%]">
                                      Actions
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white">
                                  {(fieldData?.invoiceDetails || []).map((row, index) => {
                                    const selectedParty = allCounterPartyList.find(
                                      (cp) => String(cp._id || cp.id) === String(row.counterPartyId)
                                    );
                                    const isRowDisabled = isReadOnly || row.isInvoiceGenerated;

                                    return (
                                      <tr key={row._id || index} className="h-[19px] hover:bg-slate-50/50 transition-colors">
                                        {/* Counterparty Cell */}
                                        <td className={`p-0 h-[19px] align-middle border-r border-b border-[#cbd5e1] ${!row.counterPartyId ? 'bg-[#ffffd6]' : 'bg-[#f0f4f9]'}`}>
                                          {row.counterPartyId && selectedParty ? (
                                            <div className="flex items-center justify-between gap-1.5 w-full h-[19px] px-1.5 bg-[#f0f4f9]">
                                              <div className="flex items-center gap-1.5 min-w-0">
                                                {selectedParty.type === "Individual" ? (
                                                  <FiUser className="w-3 h-3 text-sky-600 flex-shrink-0" />
                                                ) : (
                                                  <MdOutlineBusiness className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                                                )}
                                                <span className="font-medium text-slate-800 truncate text-[11px]">
                                                  {row.name || selectedParty.name || "Counterparty"}
                                                </span>
                                              </div>
                                              {!isRowDisabled && (
                                                <div className="flex items-center gap-0.5 flex-shrink-0">
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      setCounterpartyModal({
                                                        isOpen: true,
                                                        fieldKey: field.key,
                                                        fieldLabel: field.label,
                                                        index,
                                                        currentId: row.counterPartyId,
                                                      })
                                                    }
                                                    className="p-0.5 text-slate-400 hover:text-[#313C4C] rounded-none transition-colors"
                                                    title="Change counterparty"
                                                  >
                                                    <FiEdit2 className="w-3 h-3" />
                                                  </button>
                                                  <button
                                                    type="button"
                                                    onClick={() => handleClearCounterparty(field.key, index)}
                                                    className="p-0.5 text-slate-400 hover:text-rose-600 rounded-none transition-colors"
                                                    title="Clear selection"
                                                  >
                                                    <FiX className="w-3 h-3" />
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          ) : (
                                            <button
                                              type="button"
                                              disabled={isRowDisabled}
                                              onClick={() =>
                                                setCounterpartyModal({
                                                  isOpen: true,
                                                  fieldKey: field.key,
                                                  fieldLabel: field.label,
                                                  index,
                                                  currentId: "",
                                                })
                                              }
                                              className={`w-full h-[19px] px-1.5 rounded-none border-none outline-none text-[11px] font-medium text-left flex items-center justify-between transition-all ${isRowDisabled
                                                ? "bg-[#f0f4f9] text-slate-400 cursor-not-allowed"
                                                : "bg-[#ffffd6] text-[#313C4C] hover:bg-sky-50"
                                                }`}
                                            >
                                              <span className="flex items-center gap-1.5">
                                                <FiPlus className="w-3 h-3 text-sky-600" />
                                                Select Counterparty
                                              </span>
                                            </button>
                                          )}
                                        </td>

                                        {/* Description Cell */}
                                        <td className="p-0 h-[19px] align-middle border-r border-b border-[#cbd5e1] bg-[#ffffd6] focus-within:bg-white focus-within:ring-1 focus-within:ring-amber-500 focus-within:ring-inset focus-within:z-10 relative">
                                          <input
                                            type="text"
                                            value={row.description || row.desc || ""}
                                            disabled={isRowDisabled}
                                            onChange={(e) =>
                                              handleDescriptionChange(field.key, index, e.target.value)
                                            }
                                            placeholder="Description / notes..."
                                            className="w-full h-[19px] block px-2 py-0 rounded-none border-none outline-none focus:outline-none focus:ring-0 bg-transparent text-slate-800 placeholder:text-slate-400 disabled:bg-[#f0f4f9] disabled:text-slate-400 disabled:cursor-not-allowed font-medium text-[11px] cargo-table-input"
                                          />
                                        </td>

                                        {/* Amount Cell */}
                                        <td className="p-0 h-[19px] align-middle border-r border-b border-[#cbd5e1] bg-[#ffffd6] focus-within:bg-white focus-within:ring-1 focus-within:ring-amber-500 focus-within:ring-inset focus-within:z-10 relative">
                                          <input
                                            type="number"
                                            value={row.amt === 0 ? "" : row.amt || ""}
                                            disabled={isRowDisabled || !row.counterPartyId}
                                            onChange={(e) =>
                                              handleAmountChange(field.key, index, e.target.value)
                                            }
                                            placeholder="0.00"
                                            min="0"
                                            step="0.01"
                                            className="w-full h-[19px] block px-2 py-0 rounded-none border-none outline-none focus:outline-none focus:ring-0 bg-transparent text-slate-800 placeholder:text-slate-400 disabled:bg-[#f0f4f9] disabled:text-slate-400 disabled:cursor-not-allowed font-medium text-[11px] cargo-table-input"
                                          />
                                        </td>

                                        {/* Actions Cell */}
                                        <td className="px-1 py-0 h-[19px] align-middle text-right whitespace-nowrap border-b border-[#cbd5e1] bg-white">
                                          <div className="flex items-center justify-end gap-1.5">
                                            {row.isInvoiceGenerated && (
                                              <div
                                                title="Invoice already generated. This row is locked."
                                                className="text-sky-600 hover:text-sky-800 p-0.5"
                                              >
                                                <FiInfo className="w-3.5 h-3.5" />
                                              </div>
                                            )}
                                            <button
                                              type="button"
                                              onClick={() => handleDeleteRow(field.key, index)}
                                              disabled={
                                                isRowDisabled ||
                                                (fieldData?.invoiceDetails?.length || 0) <= 1
                                              }
                                              className={`p-0.5 rounded-none transition-colors ${isRowDisabled ||
                                                (fieldData?.invoiceDetails?.length || 0) <= 1
                                                ? "text-slate-300 cursor-not-allowed"
                                                : "text-slate-400 hover:text-rose-600 hover:bg-rose-50 cursor-pointer"
                                                }`}
                                              title="Delete allocation row"
                                            >
                                              <FiTrash2 className="w-3 h-3" />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            {/* Subtable Footer / Actions */}
                            <div className="flex flex-wrap items-center justify-between gap-2 px-2.5 py-1.5 bg-[#f0f4f9] border-t border-[#cbd5e1]">
                              <button
                                type="button"
                                onClick={() => handleAddRow(field.key)}
                                disabled={
                                  isReadOnly ||
                                  availableCounterpartiesForField.length === 0
                                }
                                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-none bg-[#313C4C] text-white hover:bg-[#1e2632] transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <FiPlus className="w-3 h-3" />
                                <span>Add Counterparty</span>
                              </button>

                              <div className="flex items-center gap-2 sm:gap-3 text-[11px] font-medium">
                                <span className="text-slate-600">
                                  Total: <span className="text-slate-800 font-medium">{formatNumber(fieldTotal)}</span>
                                </span>
                                <span className="text-slate-300">|</span>
                                <span className="text-slate-600">
                                  Allocated: <span className="text-[#313C4C] font-medium">{formatNumber(allocatedTotal)}</span>
                                </span>
                                <span className="text-slate-300">|</span>
                                <span className="text-slate-600">
                                  Remaining:{" "}
                                  <span className={remainingAmount > 0 ? "text-amber-600 font-medium" : "text-emerald-600 font-medium"}>
                                    {formatNumber(remainingAmount)}
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Counterparty Selection Modal */}
      {counterpartyModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-[1px] flex items-center justify-center z-[99999] p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-none shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-[#cbd5e1] overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between w-full px-3 py-2 bg-[#f8fafc] border-b border-[#cbd5e1] text-[#313C4C]">
              <div>
                <h3 className="text-[14px] font-medium tracking-wide text-slate-800 m-0">
                  Select Counterparty
                </h3>
                <p className="text-[11px] font-medium text-slate-500 mt-0.5 m-0">
                  {counterpartyModal.fieldLabel} — Row #{counterpartyModal.index + 1}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setCounterpartyModal(null)}
                className="flex items-center gap-1 text-slate-600 hover:text-slate-900 px-2 py-1 rounded-none border border-[#cbd5e1] hover:bg-slate-100 transition-all cursor-pointer text-[11px] font-medium capitalize"
                aria-label="Close modal"
              >
                <MdClose size={14} />
                <span className="hidden md:block text-[11px] font-medium capitalize">
                  Close
                </span>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-3 overflow-y-auto flex-1 bg-white">
              <CounterPartySelect
                value={counterpartyModal.currentId}
                onChange={(e) => {
                  handleCounterpartySelect(
                    counterpartyModal.fieldKey,
                    counterpartyModal.index,
                    e.target.value
                  );
                  setCounterpartyModal(null);
                }}
                counterPartyDetails={getAvailableCounterparties(
                  counterpartyModal.fieldKey,
                  counterpartyModal.currentId
                )}
                isMultiSelect={false}
                allowCreate={false}
                onCounterPartyAdded={fetchCounterPartyDetails}
              />
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end items-center gap-2 px-3 py-2 border-t border-[#cbd5e1] bg-white">
              <button
                type="button"
                onClick={() => setCounterpartyModal(null)}
                className="px-4 py-1 text-[11px] font-medium capitalize text-slate-700 hover:bg-slate-100 border border-[#cbd5e1] rounded-none transition cursor-pointer"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesInvoiceModel;
